import { ApiError } from '../data/live';

/*
 * What a refusal from the server means, in a sentence an operator can act on.
 *
 * The server answers with machine codes ("station_not_live") on purpose: they
 * are stable, greppable and the same for every client. They were also, until
 * this file, what the console showed — so an operator who tried to publish a
 * charge point under a test site was told `station_not_live` and had to guess.
 * Worse, several dialogs showed nothing at all and simply stayed open.
 *
 * One map rather than a ternary chain per screen: the same code (reason_required,
 * already_settled) is returned by several routes, and nine screens each keeping
 * their own partial translation is how they came to disagree.
 *
 * An unknown code is never swallowed. It is shown as it came, so a new refusal
 * the console has not caught up with is still something an operator can quote
 * to whoever is on call — "Could not save" with the code thrown away is not.
 */
const MESSAGES: Record<string, string> = {
  /* transport */
  network: 'Could not reach the server. Check the connection and try again.',
  timeout: 'The server did not answer in time. Try again.',
  upstream_unavailable: 'The charging system (CSMS) is not answering. Try again shortly.',
  upstream_timeout: 'The charging system (CSMS) did not answer in time. Try again.',
  forbidden: 'Your role is not allowed to do that.',
  not_found: 'That record no longer exists. Refresh the page.',
  too_many_requests: 'Too many attempts. Wait a minute and try again.',
  invalid_request: 'The server did not accept that request.',
  nothing_to_change: 'Nothing changed.',
  nothing_to_set: 'Nothing changed.',

  /* charge points */
  prod_needs_station: 'Give it a station before making it live — drivers need an address to navigate to.',
  prod_needs_coordinates: 'That station has no coordinates. Add latitude and longitude to the station before making anything on it live.',
  station_not_live: 'That station is still Test. Make the station live first — a charge point cannot be live on a test site.',
  unknown_station: 'That station no longer exists or has been archived.',
  unknown_model: 'That model is not in the catalog. Pick one from the list, or add it under Charger models first.',
  max_simultaneous_min_1: 'Connectors at once must be a whole number, 1 or more.',
  price_negative: 'A price cannot be negative.',
  bad_environment: 'Visibility must be Test or Live.',
  reason_required: 'Give a reason — at least a few words, so it can be reviewed later.',
  unknown_charge_point: 'The CSMS does not know that charge point.',
  charge_point_blocked: 'That charge point is blocked. Reactivate it first.',
  already_registered: 'That identity is already recorded.',
  already_connected: 'A charger with that identity has already connected, so it is on this list.',
  charge_point_id_charset: 'Letters, digits and _ . : - only.',
  charge_point_id_required: 'Enter the identity the charger will announce.',
  has_transactions: 'It has sessions recorded against it. Tick the box to delete them too.',
  online: 'It reconnected while this dialog was open.',
  confirm_mismatch: 'That is not the identity of this charge point.',
  csms_refused: 'The CSMS refused: something still references it.',

  /* connectors */
  bad_connector_id: 'That is not a valid connector number.',
  bad_connector: 'That connector type is not recognised.',
  power_type_ac_or_dc: 'Current must be AC or DC.',
  max_watts_positive: 'Power must be more than zero.',

  /* commands and sessions */
  not_online: 'The charger is not connected, so it cannot take a command.',
  never_connected: 'The charger has never connected.',
  charger_unreachable: 'The charger did not answer.',
  charger_offline: 'The charger is not connected, so it cannot say whether this session is still running. It is held open until it returns.',
  charger_still_running: 'The charger still reports this session as running. Stop it at the charger instead.',
  connector_status_unreliable: 'This charger model reports a connector as Available even while it is charging, so that is not evidence the session is over. It stays open until the charger stops it.',
  session_held: 'The session is being held open through an outage.',
  no_open_session: 'That session is already closed.',
  already_charging: 'That connector is already charging.',
  session_not_stoppable: 'That session cannot be stopped from here.',

  /* stations */
  name_required: 'A name is required.',
  name_too_short: 'That name is too short.',
  coordinates_need_both: 'Latitude and longitude go together.',
  bad_coordinates: 'Those coordinates are not valid.',

  /* drivers, vehicles, documents */
  driver_already_exists: 'A driver with that email or phone already exists. Search for them instead.',
  email_or_phone_required: 'Give an email, a phone, or both.',
  bad_email: 'That email address is not valid.',
  bad_phone: 'That phone number is not valid.',
  phone_taken: 'That phone number already belongs to another account.',
  email_taken: 'An account with that email already exists.',
  bad_doc_status: 'That is not a valid document status.',
  bad_status: 'That is not a valid status.',
  already_decided: 'Someone has already decided this one. Refresh to see the outcome.',
  plate_required: 'A plate is required.',
  too_many_vehicles: 'This driver already has the most vehicles allowed.',
  tag_already_exists: 'That card number is already issued.',
  card_number_required: 'Enter the card number.',
  id_tag_too_long: 'That card number is too long.',
  vehicle_is_charging: 'That vehicle is charging right now. Try again when it stops.',
  no_such_driver: 'That driver no longer exists.',
  no_such_vehicle: 'That vehicle no longer exists.',
  no_such_tag: 'That card no longer exists.',

  /* agents, top-ups, float */
  area_required: 'Give the counter an area.',
  commission_pct_range: 'Commission percent must be between 0 and 100.',
  commission_fixed_range: 'The fixed commission is out of range.',
  bad_approval: 'That is not a valid decision.',
  no_such_agent: 'That counter no longer exists.',
  contact_taken: 'That email or phone already belongs to another counter.',
  code_taken: 'That code is already in use. Try again.',
  already_settled: 'This has already been settled by someone else. Refresh to see the outcome.',
  expired: 'This request has expired.',
  amount_invalid: 'Enter an amount greater than zero.',
  insufficient_float: 'The counter does not hold enough float for that.',
  request_already_open: 'There is already an open request for this counter.',
  decision_required: 'Choose approve or reject.',

  /* users */
  bad_role: 'That is not a valid role.',
  cannot_demote_yourself: 'You cannot change your own role or suspend yourself.',

  /* releases */
  force_needs_published_build: 'Publish a build first. Forcing an update with nothing newer to install locks everyone out of the app.',
  no_build_uploaded: 'Upload a build first. Publishing with no file would offer phones an update they cannot download.',
  build_not_newer: 'That build number is not newer than the current one.',
  not_an_apk: 'That file is not an APK.',
  too_large: 'That file is too large.',
  rollout_range: 'Rollout must be between 0 and 100 percent.',
  release_not_published: 'That release is not published yet.',
  no_such_release: 'There is no release row for that app and platform.',
  bad_version: 'Version looks like 1.2.0.',
  bad_build: 'Build is a whole number.',
  empty_upload: 'The file was empty.',

  /* settings and catalogs */
  unknown_key: 'One of those settings is not known to the server.',
  osrm_url_must_be_http: 'The OSRM endpoint must start with http:// or https://',
  bad_tiles_provider: 'Map tiles must be OSM or Google.',
  bad_routing_provider: 'Routing must be OSRM or Google.',
  bad_mail_port: 'The mail port must be a number between 1 and 65535.',
  bad_mail_security: 'Mail security must be STARTTLS, TLS or None.',
  bad_email_address: 'That email address is not valid.',
  otp_needs_mail: 'Turn email sending on before requiring sign-in codes.',
  bad_amount: 'Enter an amount in whole currency, with at most two decimals.',
  full_hold_below_minimum: 'The largest Full hold cannot be below the minimum balance to start.',
  bad_stop_margin: 'The stop margin must be between 5 and 120 seconds.',
  float_would_go_negative: 'That would take the counter\'s float below zero.',
  bad_currency: 'A currency is a three-letter code, such as ETB.',
  title_required: 'Give the document a title.',
  body_too_short: 'That text is too short to be the document — at least 50 characters.',
  body_too_long: 'That text is over 100,000 characters.',
  model_taken: 'That model is already in the catalog.',
  model_required: 'Give the model a name.',
  manufacturer_required: 'Give the manufacturer.',
};

/** The code a failure carries, or a stand-in for one that never reached the
 *  server. */
function codeOf(e: unknown): string {
  if (e instanceof Error) {
    // fetch rejects with a TypeError when the network is down, and the
    // AbortController in `req` turns a slow answer into an AbortError.
    if (e.name === 'AbortError') return 'timeout';
    if (e instanceof TypeError && /fetch|network/i.test(e.message)) return 'network';
    return e.message;
  }
  return typeof e === 'string' ? e : '';
}

/**
 * A failure, as an operator should read it.
 *
 * `overrides` is for the screen that knows better than the shared wording — a
 * removal dialog can say "tick the box" because it has a box. Order: the
 * screen's own words, then this map, then the server's own sentence, then the
 * bare code.
 */
export function errorText(e: unknown, overrides?: Record<string, string>): string {
  const code = codeOf(e);
  if (overrides?.[code]) return overrides[code]!;
  if (MESSAGES[code]) return MESSAGES[code]!;
  const detail = e instanceof ApiError ? e.detail : undefined;
  if (detail) return `${detail} (${code})`;
  return code ? `The server refused: ${code}` : 'Something went wrong. Try again.';
}

/** For a query that failed to load: a hint line under an Empty title. */
export function loadFailedHint(e: unknown): string {
  return `${errorText(e)} Nothing is shown rather than stale numbers.`;
}
